import { SyntheticEvent, useCallback, useEffect, useRef } from "react";
import classNames from "classnames";
import Link from "next/link";
import { ColorName, ServiceIcon, Text } from "@zapier/design-system";
import { Icon, IconName } from "@zapier/zinnia-icons";
import {
  NodeType,
  DefaultNodeData,
  ZapMetadata,
  floatingToolbarActiveButtonAtom,
  serviceAtomFamily,
  ZapNodeData,
  determineAssetType,
  saveCanvasSetter,
  toggleZapStepNodesAndEdgesSetterAtomFamily,
  nodesAtom,
  hasZslAtom,
  getAssetId,
} from "@zapier/canvas-state";
import { useAtomValue, useAtom, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { NodeProps, Node } from "@xyflow/react";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";

import { StepTitle } from "../../../../components/StepTitle";
import capitalizeFirstLetter from "../../../../utils/capitalizeFirstLetter";
import { getDefaultColorForAssetType } from "../../../../utils/getDefaultColorForAssetType";
import { usePreviewContext } from "../../../../context/PreviewProvider";
import { UnstyledButton } from "../../../UnstyledButton";
import { truncateUrl } from "../../../../utils/truncateUrl";
import { isDraftOrTemplateNode } from "../../../../utils/isDraftOrTemplateNode";
import { AssetAccessChecker } from "../../../AssetAccessChecker";
import { ToggleZapSteps } from "../../../ToggleZapSteps";
import { AssetLink } from "../../../AssetLink";
import { ActorLabel } from "../../../ActorLabel";

import { AssetPreview } from "./AssetPreview";
import { getContrastingTextColor } from "./utils";
import { NotesButton } from "./NotesButton";
import styles from "./AssetNodeSkeleton.module.css";

type Props = {
  className: string;
  placeholder: string;
  zap?: ZapMetadata;
  isZapExpanded?: boolean;
  isZapNotFound?: boolean;
  isViewOnlyMode?: boolean;
  iconName?: IconName;
  onUpdateNode?: (
    data: Partial<ZapNodeData>,
    options?: { shouldSave: boolean },
  ) => void;
  backgroundColor?: string;
  onMouseEnter?: () => void;
};

export function AssetNodeSkeleton(
  props: NodeProps<Node<DefaultNodeData | ZapNodeData>> & Props,
) {
  const { isPreview } = usePreviewContext();
  const {
    id,
    data,
    type,
    selected,
    className,
    isViewOnlyMode = false,
    iconName,
    zap,
    onUpdateNode,
    backgroundColor,
    isZapNotFound,
    onMouseEnter,
  } = props;
  const hasNotes = !isRemirrorJSONEmpty(data.notesJson);

  const assetType = determineAssetType(
    (data as DefaultNodeData).services,
    type as NodeType,
  );
  const {
    link,
    color = getDefaultColorForAssetType(assetType),
    label,
    tableId,
    projectId,
    chatbotId,
    agentId,
    actors,
    proposalId,
  } = data as DefaultNodeData;

  const { isExpanded } = data as ZapNodeData;
  const actor = actors?.[0];

  const isDraftNodeOrTemplate = isDraftOrTemplateNode(id);
  const hasZsl = useAtomValue(hasZslAtom);

  // Need hasZsl or else the data we need might not be there when you click the button
  // Need !isPreview or else the button shows up in the ZapPreview and does nothing
  const showAssetDetailsButtonInDraft =
    Boolean(hasZsl || proposalId) &&
    isDraftNodeOrTemplate &&
    !isPreview &&
    assetType !== "chatbot";

  const showAssetDetailsButtonInNonDraft =
    !isDraftNodeOrTemplate && !isZapNotFound;

  const appId =
    (data as DefaultNodeData).services?.[0]?.currentImplementation.id ?? "";

  const [activeButton, setActiveButton] = useAtom(
    floatingToolbarActiveButtonAtom,
  );
  const app = useAtomValue(serviceAtomFamily(appId));
  const assetId = getAssetId(data, assetType);
  const isAgentNodeWithAgentId = assetType === "agent" && assetId;
  const isBlankAgentNode = assetType === "agent" && !assetId;

  const toggleFloatingLinkInput = useCallback(() => {
    const isAlreadyOpen = "link" === activeButton;
    setActiveButton(isAlreadyOpen ? null : "link");
  }, [activeButton, setActiveButton]);

  const zapTitle = zap?.title ?? "";
  const hasSetZapTitleAsStepTitle = useRef(false);

  useEffect(() => {
    // If there was no step title, set the Zap title as the step title
    if (
      !hasSetZapTitleAsStepTitle.current &&
      !label &&
      zapTitle &&
      onUpdateNode
    ) {
      onUpdateNode({ label: zapTitle }, { shouldSave: true });
      /* Mark hasSetZapTitleAsStepTitle as true to prevent this logic from running again.
       * We only want this to happen on page load, not when user empties the input manually */
      hasSetZapTitleAsStepTitle.current = true;
    }
  }, [label, zapTitle, onUpdateNode]);

  const ASSET_NAME =
    assetType === "zap" ? capitalizeFirstLetter(assetType) : assetType;

  const zapSteps = zap?.steps || [];
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const toggleZapStepsAndEdges = useSetAtom(
    toggleZapStepNodesAndEdgesSetterAtomFamily(id),
  );

  /** Persist whether Zap node is expanded, so we can show the same UI on page load.
   * React Flow state updates are handled by toggleZapStepsAndEdges */
  const saveZapNodeToBE = useAtomCallback(
    useCallback(
      async (get: (atom: typeof nodesAtom) => Node[]) => {
        const nodes = get(nodesAtom);
        const updatedNodes = nodes.map((node: Node) => {
          const isZapNode = node.id === id;

          if (!isZapNode) {
            return node;
          }

          return {
            ...node,
            data: {
              ...node.data,
              isExpanded: !isExpanded,
            },
          };
        });

        saveToBackend({ nodes: updatedNodes });
      },
      [id, isExpanded, saveToBackend],
    ),
  );

  const onToggleZapSteps = useCallback(
    (event: SyntheticEvent<HTMLButtonElement | HTMLUListElement>) => {
      /** To prevent the floating toolbar from opening */
      event.stopPropagation();
      toggleZapStepsAndEdges(Boolean(isExpanded));
      saveZapNodeToBE();
    },
    [isExpanded, saveZapNodeToBE, toggleZapStepsAndEdges],
  );

  return (
    <div
      className={classNames(
        styles.container,
        styles[assetType || "default"],
        className,
      )}
      style={{
        backgroundColor: color,
      }}
      onMouseEnter={onMouseEnter}
    >
      {(tableId || projectId || chatbotId || agentId) && (
        <AssetAccessChecker
          tableId={tableId}
          interfaceProjectId={projectId}
          chatbotId={chatbotId}
          agentId={agentId}
          assetType={assetType}
        />
      )}
      <div className={styles.bodyContainer}>
        <div className={styles.whiteBackground}>
          <div
            className={styles.body}
            style={{
              backgroundColor,
            }}
          >
            {/* Don't show actor label for agent nodes as it blocks the step title */}
            {actor && assetType !== "agent" && (
              <div className={styles.actorLabelWrapper}>
                <ActorLabel actor={actor} />
              </div>
            )}
            {/* hide in ZapPreview since the ToggleZapSteps doesn't work  */}
            {!isPreview && zapSteps && zapSteps.length > 0 && (
              <div className={styles.toggleZapStepsWrapper}>
                <ToggleZapSteps
                  onClick={onToggleZapSteps}
                  isExpanded={Boolean(isExpanded)}
                  steps={zapSteps}
                />
              </div>
            )}
            <div
              className={styles.previewImage}
              style={
                !assetType ? { backgroundColor: `#${app?.primary_color}` } : {}
              }
            >
              {assetType &&
                (assetType !== "agent" || isAgentNodeWithAgentId) && (
                  <AssetPreview
                    id={id}
                    assetType={assetType}
                    assetId={assetId}
                    showAssetDetailsButton={
                      showAssetDetailsButtonInDraft ||
                      showAssetDetailsButtonInNonDraft
                    }
                    projectId={(data as DefaultNodeData).projectId}
                  />
                )}
              {app?.images?.url_128x128 && !assetType && (
                <div className={styles.serviceIconWrapper}>
                  <ServiceIcon
                    serviceName={app?.name}
                    size="large"
                    src={app?.images?.url_128x128}
                  />
                </div>
              )}
            </div>
            {!isAgentNodeWithAgentId && (
              <StepTitle
                nodeId={id}
                label={data.label}
                isSelected={Boolean(selected)}
                placeholder={props.placeholder}
                canNodeGetAiSuggestion={false}
                rows={2}
                isViewOnlyMode={isViewOnlyMode || isPreview}
                isBlankAgentNode={isBlankAgentNode}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        {iconName && (
          <div className={styles.iconContainer}>
            <Icon name={iconName} color="BrandOrange" size={16} />
          </div>
        )}
        {app?.images?.url_16x16 && !assetType && (
          <ServiceIcon
            serviceName={app?.name}
            size="small"
            src={app?.images?.url_16x16}
          />
        )}
        {!link && (
          <Text
            type="MinimalPrint1"
            color={getContrastingTextColor(color!) as ColorName}
          >
            <div
              className={classNames(styles.truncateText, {
                [styles.truncateTextNoNotes]: !hasNotes,
              })}
            >
              {capitalizeFirstLetter((assetType || app?.name) ?? "")}
            </div>
          </Text>
        )}
        {link && (
          <>
            <UnstyledButton
              onClick={toggleFloatingLinkInput}
              disabled={isViewOnlyMode}
            >
              <Text
                color={getContrastingTextColor(color!) as ColorName}
                type="MinimalPrint1"
              >
                {truncateUrl(link)}
              </Text>
            </UnstyledButton>
            <Link
              href={link}
              target="_blank"
              className={styles.offsiteLink}
              style={
                {
                  color: getContrastingTextColor(color!, true),
                } as React.CSSProperties
              }
            >
              <Icon name="arrowOffsite" size={15} />
            </Link>
          </>
        )}
        {!isDraftNodeOrTemplate && assetId && (
          <AssetLink
            assetType={assetType}
            assetId={assetId}
            assetName={ASSET_NAME}
            interfaceProjectId={(data as DefaultNodeData).projectId}
            isViewOnlyMode={isViewOnlyMode}
            color={color}
          />
        )}
        {assetType && !assetId && (
          <UnstyledButton
            onClick={toggleFloatingLinkInput}
            disabled={isViewOnlyMode}
          >
            <Text
              type="MinimalPrint1"
              color={getContrastingTextColor(color!) as ColorName}
              className={styles.underline}
            >
              Link existing
            </Text>
          </UnstyledButton>
        )}
        <NotesButton
          id={id}
          hasNotes={hasNotes}
          isViewOnlyMode={isViewOnlyMode}
        />
      </div>
    </div>
  );
}
